import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { QueryStatus } from '@reduxjs/toolkit/query'

import {
  ApiCall,
  ChangePasswordReqPayload,
  ReqBodyPayloadType,
  SettingsErrors,
  SettingsFields,
  Tab,
  UpdateUserDataReqPayload,
} from '~/settings-scenario/types'
import { UserType } from '~/types/user-info-types'
import { useUpdateUserMutation } from '~/redux/apis/user-api'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useChangePasswordMutation } from '~/auth/apis/auth.api'
import { validateBirthYear, validateEmail } from '~/utils/helpers'
import { logoutUser } from '~/auth/slices/auth.slice'

import homApi from '~/services/hom-api.service'

import { useGetCurriculumCountryCodeFromCurriculumName } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { setCurriculumName } from '~/curriculum/slice/persistCurriculum.slice'
import { getStateCodeForCurriculumName } from '~/curriculum/constants/usCurriculumStates.const'

import { useCanChangePassword } from '~/settings-scenario/hooks/useCanChangePassword'

import {
  selectFields,
  setErrorsActionCreator,
  setIsServerErrorInNicknameTabActionCreator,
  setServerErrorActionCreator,
} from '../redux/settings.slice'

export function useUpdateSettings(): (currentTab: Tab) => Promise<boolean> {
  const { t } = useTranslation(['validation', 'errors'])
  const user = useCurrentUser()
  const id = user?.id
  const dispatch = useAppDispatch()

  const [updateUserData, { isLoading }] = useUpdateUserMutation()
  const [changePassword, passwordUpdateResponse] = useChangePasswordMutation()

  const fields = useAppSelector(selectFields)
  const canChangePassword = useCanChangePassword()

  const curriculumCountryCode = useGetCurriculumCountryCodeFromCurriculumName(fields.curriculum)

  useEffect(() => {
    if (!passwordUpdateResponse.isLoading && passwordUpdateResponse.status !== QueryStatus.uninitialized) {
      if (passwordUpdateResponse.data && passwordUpdateResponse.data.type === 'success') {
        dispatch(logoutUser())
        // eslint-disable-next-line fp/no-mutating-methods
        return
      }
      if (passwordUpdateResponse?.error?.message) {
        dispatch(setServerErrorActionCreator(passwordUpdateResponse.error.message))
        dispatch(setIsServerErrorInNicknameTabActionCreator(false))
      }
    }
  }, [passwordUpdateResponse, passwordUpdateResponse.isSuccess, dispatch])

  function validateAndSetErrors(currentTab: Tab): boolean {
    dispatch(setErrorsActionCreator({}))
    dispatch(setServerErrorActionCreator(null))
    dispatch(setIsServerErrorInNicknameTabActionCreator(null))
    let pageFields: Partial<SettingsFields> = {}
    const pageErrors: SettingsErrors = {}

    switch (currentTab) {
      case Tab.PERSONAL_INFO:
        pageFields = {
          email: fields.email,
          birthYear: fields.birthYear,
          phone: fields.phone ? fields.phone : undefined,
        }

        Object.entries(pageFields).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim().length === 0) {
            pageErrors[key] = t('validation:fieldRequired')
          }
        })

        if (!validateEmail(fields.email)) {
          pageErrors.email = t('validation:validEmail')
        }

        if (
          isNaN(Number(fields.birthYear)) ||
          !Number.isInteger(Number(fields.birthYear)) ||
          !validateBirthYear(Number(fields.birthYear))
        ) {
          pageErrors['birthYear'] = t('validation:birthYear')
        }

        if (Object.values(pageErrors).length === 0 && !isLoading) {
          return true
        }
        break

      case Tab.PROFILE:
        if (!isLoading) {
          return true
        }
        break

      case Tab.CURRICULUM:
        if (!isLoading) {
          return true
        }
        break

      case Tab.ONBOARDING_EMAIL:
        pageFields = {
          email: fields.email,
        }

        Object.entries(pageFields).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim().length === 0) {
            pageErrors[key] = t('validation:fieldRequired')
          }
        })

        if (!validateEmail(fields.email)) {
          pageErrors.email = t('validation:validEmail')
        }

        if (Object.values(pageErrors).length === 0 && !isLoading) {
          return true
        }

        break

      case Tab.AVATAR_NICKNAME:
        pageFields = {
          nickname: fields.nickname,
        }

        Object.entries(pageFields).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim().length === 0) {
            pageErrors[key] = t('validation:fieldRequired')
          }
        })

        if (Object.values(pageErrors).length === 0 && !isLoading) {
          return true
        }
        break

      case Tab.CHANGE_PASSWORD:
        pageFields = {
          currPassword: fields.currPassword,
          newPassword: fields.newPassword,
          newPasswordConfirm: fields.newPasswordConfirm,
        }

        Object.entries(pageFields).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim().length === 0) {
            pageErrors[key] = t('validation:fieldRequired')
          }
        })

        if (pageFields.newPassword !== pageFields.newPasswordConfirm) {
          pageErrors.newPasswordConfirm = t('passwordMatch')
        }

        if (Object.values(pageErrors).length === 0 && !isLoading) {
          return true
        }
        break
    }
    dispatch(setErrorsActionCreator(pageErrors))
    return false
  }

  function getRequestBody(currentTab: Tab): {
    apiCallType: ApiCall.UPDATE_USER_DATA | ApiCall.CHANGE_PASSWORD
    payload: ReqBodyPayloadType
  } {
    switch (currentTab) {
      case Tab.PERSONAL_INFO:
        return {
          apiCallType: ApiCall.UPDATE_USER_DATA,
          payload: {
            id: id as number,
            data: {
              email: fields.email,
              birthYear: Number(fields.birthYear),
              wantsNewsletter: fields.wantsNewsletter,
              phone: fields.phone ? fields.phone : undefined,
            },
          },
        }

      case Tab.PROFILE:
        return {
          apiCallType: ApiCall.UPDATE_USER_DATA,
          payload: {
            id: id as number,
            data: {
              userType: fields.userType ? (fields.userType as UserType) : undefined,
              skill: fields.skill ? fields.skill : undefined,
              country: fields.country,
              // A pre-column US user sees an empty select; saving must not flip them to the default curriculum.
              curriculumCountryCode: fields.curriculum ? curriculumCountryCode : undefined,
              curriculumStateCode: getStateCodeForCurriculumName(fields.curriculum),
            },
          },
        }

      case Tab.CURRICULUM:
        return {
          apiCallType: ApiCall.UPDATE_USER_DATA,
          payload: {
            id: id as number,
            data: {
              curriculumCountryCode: fields.curriculum ? curriculumCountryCode : undefined,
              curriculumStateCode: getStateCodeForCurriculumName(fields.curriculum),
            },
          },
        }
      case Tab.ONBOARDING_EMAIL:
        return {
          apiCallType: ApiCall.UPDATE_USER_DATA,
          payload: {
            id: id as number,
            data: {
              email: fields.email,
            },
          },
        }

      case Tab.AVATAR_NICKNAME:
        return {
          apiCallType: ApiCall.UPDATE_USER_DATA,
          payload: {
            id: id as number,
            data: {
              nickname: fields.nickname,
            },
          },
        }

      case Tab.CHANGE_PASSWORD:
        return {
          apiCallType: ApiCall.CHANGE_PASSWORD,
          payload: {
            currentPassword: fields.currPassword,
            password: fields.newPassword,
          },
        }
    }
  }

  function callApi(apiCallType: number, payload: ReqBodyPayloadType): Promise<any> | null {
    switch (apiCallType) {
      case ApiCall.UPDATE_USER_DATA:
        return updateUserData(payload as UpdateUserDataReqPayload)

      case ApiCall.CHANGE_PASSWORD:
        return changePassword(payload as ChangePasswordReqPayload)

      default:
        return null
    }
  }

  async function handleOnSubmit(currentTab: Tab): Promise<boolean> {
    if (!id) {
      return false
    }

    // Never reach PATCH /v1/auth/change-password for a school student, whatever set the tab.
    if (currentTab === Tab.CHANGE_PASSWORD && !canChangePassword) {
      return false
    }

    const validationPassed = validateAndSetErrors(currentTab)

    if (!validationPassed) {
      return false
    }

    const requestBody = getRequestBody(currentTab)

    if (requestBody.apiCallType === ApiCall.UPDATE_USER_DATA && requestBody.payload.data.email) {
      const { exists: emailAlreadyInUse } = await homApi.getUserWithEmailExists(requestBody.payload.data.email)

      if (emailAlreadyInUse) {
        dispatch(setErrorsActionCreator({ email: t('validation:emailAlreadyInUse') }))
        return false
      }
    }

    {
      try {
        const res = await callApi(requestBody.apiCallType, requestBody.payload)

        if (res?.error?.error != null || res?.error?.data?.message) {
          const errorMessage = res?.error?.data?.message || t('errors:unknown')
          dispatch(setServerErrorActionCreator(errorMessage))

          if (currentTab === Tab.AVATAR_NICKNAME) {
            dispatch(setIsServerErrorInNicknameTabActionCreator(true))
          } else {
            dispatch(setIsServerErrorInNicknameTabActionCreator(false))
          }

          if (res?.error?.data?.details) {
            const errors = res?.error?.data?.details
            const pageErrors: SettingsErrors = {}

            Object.keys(errors).forEach((key) => {
              pageErrors[key] = errors[key]
            })

            dispatch(setErrorsActionCreator(pageErrors))
          }
          return false
        } else {
          // Store curriculum name in persistent state when curriculum is updated
          if ((currentTab === Tab.PROFILE || currentTab === Tab.CURRICULUM) && fields.curriculum) {
            dispatch(setCurriculumName(fields.curriculum))
          }
          return true
        }
      } catch (error) {
        dispatch(setServerErrorActionCreator(error.message))
        return false
      }
    }
  }

  return handleOnSubmit
}
